import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SortingState } from "@tanstack/react-table";
import {
  DateRange,
  isPresetValue,
  parseDateRange,
} from "@/components/shared/date-range-picker-v2";
import { FieldFilter } from "@/v2/components/filter-menu/FieldFilter.model";
import { ExploreAggInteractionRow } from "@/v2/entities/explore/Explore.model";
import {
  ExploreGroupByColumnType,
  isExploreGroupByColumnType,
} from "@/v2/entities/explore/columns/ExploreColum.model";
import { ExploreFiltersValue } from "../explore-filter-menu/ExploreFilterMenu";
import {
  addFiltersToSearchParams,
  extractFiltersFromSearchParams,
} from "../explore-filter-menu/explore-filter.utils";
import { ExploreState, defaultState } from "./ExploreState.model";
import { useHandleGroupByClick } from "./useHandleGroupByClick";

export function buildSearchParams({
  groupBy,
  dateRange,
  filters,
  sorting,
}: ExploreState) {
  const searchParams = new URLSearchParams();

  if (groupBy) {
    searchParams.set("groupBy", groupBy);
  }

  if (dateRange) {
    if (dateRange.type === "custom") {
      searchParams.set("from", dateRange.dateFrom);
      searchParams.set("to", dateRange.dateTo);
    } else {
      searchParams.set("range", dateRange.value);
    }
  }

  if (sorting.length > 0) {
    const sortItem = sorting[0];
    searchParams.set("orderBy", sortItem.id);
    searchParams.set("orderDir", sortItem.desc ? "desc" : "asc");
  }

  addFiltersToSearchParams(searchParams, filters);

  return searchParams;
}

function restoreSearchParams(searchParams: URLSearchParams): ExploreState {
  const initState = { ...defaultState };

  try {
    if (searchParams.has("groupBy")) {
      const groupBy = searchParams.get("groupBy");
      searchParams.delete("groupBy");

      if (groupBy != null) {
        initState.groupBy = groupBy;
      }
    }

    // Workaround for old explore
    if (searchParams.has("by")) {
      searchParams.delete("by");
    }

    if (searchParams.has("from") && searchParams.has("to")) {
      const dateFrom = searchParams.get("from");
      searchParams.delete("from");

      const dateTo = searchParams.get("to");
      searchParams.delete("to");

      if (dateFrom != null && dateTo != null) {
        const dateFromParsed = new Date(dateFrom);
        const dateToParsed = new Date(dateTo);

        if (
          !isNaN(dateFromParsed.getTime()) &&
          !isNaN(dateToParsed.getTime())
        ) {
          initState.dateRange = {
            type: "custom",
            dateFrom: dateFromParsed.toISOString(),
            dateTo: dateToParsed.toISOString(),
          };
          initState.parsedDateRange = {
            dateFrom: dateFromParsed.toISOString(),
            dateTo: dateToParsed.toISOString(),
          };
        }
      }
    }

    if (searchParams.has("range")) {
      const range = searchParams.get("range");
      searchParams.delete("range");

      if (range != null && isPresetValue(range)) {
        initState.dateRange = { type: "preset", value: range };
        initState.parsedDateRange = parseDateRange({
          type: "preset",
          value: range,
        });
      }
    }

    if (searchParams.has("orderBy")) {
      const orderBy = searchParams.get("orderBy");
      searchParams.delete("orderBy");

      const orderDir = searchParams.get("orderDir");
      searchParams.delete("orderDir");

      if (orderBy != null) {
        initState.sorting = [
          {
            id: orderBy,
            desc: orderDir === "desc",
          },
        ];
      }
    } else {
      initState.sorting = [
        initState.groupBy == null
          ? {
              id: "createdAt",
              desc: true,
            }
          : {
              id: "volume",
              desc: true,
            },
      ];
    }

    initState.filters = extractFiltersFromSearchParams(searchParams);

    return initState;
  } catch (error) {
    return defaultState;
  }
}

const hierarchy: Record<
  ExploreGroupByColumnType,
  ExploreGroupByColumnType | undefined
> = {
  status: undefined,
  channel: undefined,
  team: "agentAssigned",
  agentAssigned: undefined,
  topic: undefined,
  subTopic: undefined,
  category: undefined,
  day: undefined,
  month: undefined,
  avgQaScore: undefined,
  avgQaScoreRoundRange: undefined,
  avgQuackMessageScoreRoundRange: undefined,
  avgSentiment: undefined,
  resolutionStatus: undefined,
  escalationReason: undefined,
  skipReason: undefined,
  resolvedReason: undefined,
  tenantId: undefined,
  week: undefined,
  isQuackAnswered: undefined,
  isAnsweredByAui: undefined,
  originUrl: undefined,
  scoreTitle: undefined,
  scoreContent: undefined,
  simulator: undefined,
  skillUsed: undefined,
  avgCsat: undefined,
};

function handleGroupByClick({
  currentState,
  groupBy,
  row,
  allowedGroupByOptions,
  notAllowedGroupByOptions,
}: {
  currentState: ExploreState;
  groupBy: string;
  row: ExploreAggInteractionRow;
  allowedGroupByOptions: ExploreGroupByColumnType[];
  notAllowedGroupByOptions: ExploreGroupByColumnType[];
}): ExploreState {
  const groupValue = row[groupBy as keyof ExploreAggInteractionRow];

  let groupByFilter: FieldFilter;

  if (groupBy === "isQuackAnswered" || groupBy === "isAnsweredByAui") {
    // Handle boolean field with eq operator
    groupByFilter =
      groupValue != null
        ? {
            operator: "eq",
            value: Boolean(groupValue),
          }
        : {
            operator: "isnull",
            value: "true",
          };
  } else {
    // Handle other fields with in operator
    groupByFilter =
      groupValue != null
        ? {
            operator: "in",
            value: [groupValue.toString()],
          }
        : {
            operator: "isnull",
            value: "true",
          };
  }

  let nextGroupBy = isExploreGroupByColumnType(groupBy)
    ? hierarchy[groupBy]
    : undefined;

  if (nextGroupBy != null && !allowedGroupByOptions.includes(nextGroupBy)) {
    nextGroupBy = undefined;
  }

  if (nextGroupBy != null && notAllowedGroupByOptions.includes(nextGroupBy)) {
    nextGroupBy = undefined;
  }

  return {
    ...currentState,
    groupBy: nextGroupBy,
    sorting: [
      {
        id: nextGroupBy != null ? "volume" : "createdAt",
        desc: true,
      },
    ],
    filters: {
      ...currentState.filters,
      [groupBy]: groupByFilter,
    },
  };
}

export function useExploreState({
  allowedGroupByOptions = [],
  notAllowedGroupByOptions = [],
  persist = true,
}: {
  allowedGroupByOptions?: ExploreGroupByColumnType[];
  notAllowedGroupByOptions?: ExploreGroupByColumnType[];
  persist?: boolean;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState(() =>
    persist ? restoreSearchParams(searchParams) : defaultState
  );

  const onGroupByClick = useHandleGroupByClick({
    state,
    allowedGroupByOptions,
    notAllowedGroupByOptions,
  });

  const handleGroupByChange = useCallback((newGroupBy?: string) => {
    setState((prevState) => ({
      ...prevState,
      groupBy: newGroupBy,
      sorting: [
        {
          id: newGroupBy != null ? "volume" : "createdAt",
          desc: true,
        },
      ],
    }));
  }, []);

  const handleFiltersChange = useCallback((newFilters: ExploreFiltersValue) => {
    setState((prevState) => ({
      ...prevState,
      filters: newFilters,
    }));
  }, []);

  const handleClearAllFilters = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      filters: {},
    }));
  }, []);

  const handleDateRangeChange = useCallback((newDateRange: DateRange) => {
    setState((prevState) => ({
      ...prevState,
      dateRange: newDateRange,
      parsedDateRange: parseDateRange(newDateRange),
    }));
  }, []);

  const reparseDateRange = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      parsedDateRange: parseDateRange(prevState.dateRange),
    }));
  }, []);

  const handleSortingChange = useCallback((updater: SortingState) => {
    setState((prevState) => ({
      ...prevState,
      sorting: updater,
    }));
  }, []);

  const handleGroupClick = useCallback(
    (row: ExploreAggInteractionRow) => {
      const nextState = onGroupByClick(row);
      if (nextState == null) {
        return;
      }

      setState(nextState);
    },
    [onGroupByClick]
  );

  useEffect(() => {
    if (persist) {
      setSearchParams(
        (prev) => {
          const next = buildSearchParams(state);
          const interactionId = prev.get("interactionId");
          if (interactionId != null) {
            next.set("interactionId", interactionId);
          }
          return next;
        },
        { replace: true }
      );
    }
  }, [state, setSearchParams]);

  return {
    state,
    handleGroupByChange,
    handleFiltersChange,
    handleDateRangeChange,
    reparseDateRange,
    handleSortingChange,
    handleGroupClick,
    handleClearAllFilters,
  };
}
